import { useRef, useState } from "react";

import { useParams } from "react-router-dom";
import { z } from "zod";

import {
  type ProjectViewLinkInvitation,
  useMutateDeleteProjectViewLinkInvitation,
  useMutateUpdateProjectViewLinkInvitation,
} from "../../../../../hooks/useProjectViewLink";
import { type WebExportBaseRole, WebExportBaseRoles } from "../../../../../hooks/useWebExport";
import useTracker from "../../../../../utils/useTracker";
import { InvitationRow } from "./InvitationRow";

interface InvitationRowProps {
  invitation: ProjectViewLinkInvitation;
}

const baseRoleSchema = z.enum([...Object.values(WebExportBaseRoles)] as [
  WebExportBaseRole,
  ...WebExportBaseRole[],
]);

const projectViewLinkInvitationSchema = z.object({
  baseRole: baseRoleSchema,
});

export const InvitationRowViewLink = ({ invitation }: InvitationRowProps) => {
  const animationPromiseResolver = useRef<(() => void) | null>(null);
  const tracker = useTracker();
  const { viewLink = "" } = useParams();

  const {
    mutateAsync: mutateUpdateViewLinktInvitation,
    isPending: isUpdatePending,
    isError: isUpdateError,
  } = useMutateUpdateProjectViewLinkInvitation({
    invitationId: invitation.invitationId,
  });
  const {
    mutateAsync: mutateDeleteProjectInvitation,
    isPending: isDeletePending,
    isError: isDeleteError,
    removeInvitationFromCache,
  } = useMutateDeleteProjectViewLinkInvitation({
    invitationId: invitation.invitationId,
    onSuccess: async () => {
      setDeleteConfirmationOpen(false);
      const animationPromise = new Promise<void>((resolve) => {
        animationPromiseResolver.current = resolve;
      });
      await animationPromise;
      removeInvitationFromCache();
    },
    onError: () => {
      setDeleteConfirmationOpen(false);
    },
  });
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const isPending = isUpdatePending || isDeletePending;

  return (
    <InvitationRow
      invitation={invitation}
      schema={projectViewLinkInvitationSchema}
      onDelete={() => {
        return mutateDeleteProjectInvitation();
      }}
      onUpdate={(data) => {
        return mutateUpdateViewLinktInvitation({
          baseRole: data.baseRole as WebExportBaseRole,
        });
      }}
      deleteConfirmationOpen={deleteConfirmationOpen}
      onRemoveIntended={() => {
        tracker.trackShareModalMemberRemoveIntended({
          viewLinkId: viewLink,
          invitationId: invitation.invitationId,
          sharedResource: "review_link",
        });
      }}
      isPending={isPending}
      isError={isUpdateError || isDeleteError}
      onOpenDeleteConfirmation={() => setDeleteConfirmationOpen(true)}
      onCloseDeleteConfirmation={() => setDeleteConfirmationOpen(false)}
      animationPromiseResolver={animationPromiseResolver}
    />
  );
};
